import React from 'react';
import { ProfilePicture } from '@getro/rombo';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'rebass/styled-components';
import pluralize from 'pluralize';

import InPortco from 'components/organisms/contactCard/inPortco';
import { ContactSocialsAndSource } from 'components/organisms/contactDetail/components/contactSocialsAndSource';
import { getContactDisplayName } from 'helpers/getContactDisplayName';
import { toStringLastChange } from '../../../services/contacts/postitions';

export const ContactAvatar = ({ variant, contact, type, trackLinkedinClick, onLinkedinClick, showEmailDropdown }) => {
  const lastChange = toStringLastChange(contact.primaryActivePosition);

  return (
    <Flex
      tx="contactAvatar"
      variant={`${variant}.container`}
      justifyContent="flex-end"
      sx={{
        columnGap: '16px',
        rowGap: '8px',
      }}
    >
      <Box tx="contactAvatar" variant={`${variant}.avatarContainer`}>
        <Box
          sx={{
            position: 'relative',
          }}
          tx="contactAvatar"
          variant={`${variant}.picture`}
          flex="0 0 auto"
        >
          <ProfilePicture
            mb="8px"
            name={getContactDisplayName(contact, 'Getro')}
            imageUrl={contact.profilePicUrl || ''}
          />
        </Box>
      </Box>
      <Box pb="8px" flexGrow="1">
        <Flex alignItems="center">
          <Text
            as="h1"
            fontWeight="600"
            fontSize={['18px', '20px']}
            color="text.dark"
            lineHeight="1.6"
            sx={{
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              display: '-webkit-box',
              '-webkit-line-clamp': ['2', '1'],
              '-webkit-box-orient': 'vertical',
            }}
          >
            {getContactDisplayName(contact)}
          </Text>
        </Flex>

        {contact.linkedinTagline && (
          <Text as="span" mr="1">
            {contact.linkedinTagline}
          </Text>
        )}
        {(contact.location || contact.yearsOfExperience) && (
          <Flex alignItems="center" flexWrap="wrap">
            {contact.location?.description && (
              <Text fontSize="1" color="text.subtle">
                {contact.location?.description}
              </Text>
            )}
            {contact.location && contact.yearsOfExperience && (
              <Box bg="text.subtle" width="2px" height="2px" mx="1" sx={{ borderRadius: 'circle' }}></Box>
            )}
            {contact.yearsOfExperience && Math.floor(contact.yearsOfExperience) > 0 && (
              <Text fontSize="1" color="text.subtle">
                {`${Math.floor(contact.yearsOfExperience)} ${pluralize(
                  'year',
                  Math.floor(contact.yearsOfExperience),
                )} of experience`}
              </Text>
            )}
            {lastChange && Math.abs(lastChange) >= 2 ? (
              <>
                <Box bg="text.subtle" width="2px" height="2px" mx="1" sx={{ borderRadius: 'circle' }}></Box>
                <Text fontSize="1" fontWeight="medium" color="success">
                  {`Last job change ${Math.abs(lastChange)} ${pluralize('year', Math.abs(lastChange))} ago`}
                </Text>
              </>
            ) : null}
          </Flex>
        )}
        {contact.inPortco && <InPortco showTooltip={type === 'matches'} />}

        <ContactSocialsAndSource
          contact={contact}
          showEmailDropdown={showEmailDropdown}
          trackLinkedinClick={trackLinkedinClick}
          onLinkedinClick={onLinkedinClick}
        />
      </Box>
    </Flex>
  );
};

ContactAvatar.propTypes = {
  contact: PropTypes.object.isRequired,
  variant: PropTypes.string,
  type: PropTypes.oneOf(['default', 'read', 'matches', 'public']),
  trackLinkedinClick: PropTypes.func,
  onLinkedinClick: PropTypes.func,
  showEmailDropdown: PropTypes.bool,
};

ContactAvatar.defaultProps = {
  variant: 'medium',
  type: 'default',
  trackLinkedinClick: () => {},
  onLinkedinClick: null,
  showEmailDropdown: true,
};
